import {
  Auth0Provider,
  Auth0ProviderOptions,
  useAuth0,
} from '@auth0/auth0-react'
import CONFIG from './config'
import { useMemo } from 'react'
import { z } from 'zod'

const { AUTH0_DOMAIN, AUTH0_CLIENTID, AUTH0_AUDIENCE } = CONFIG

export const auth0Config: Auth0ProviderOptions = {
  domain: AUTH0_DOMAIN,
  clientId: AUTH0_CLIENTID,
  authorizationParams: {
    audience: AUTH0_AUDIENCE,
    redirect_uri: window.location.origin,
  },
  cacheLocation: 'localstorage',
  useRefreshTokens: true,
}

export { Auth0Provider }

const dataAccessKey = `${CONFIG.AUTH0_AUDIENCE}/data_access`

export const dataAccessSchema = z.object({
  auth0_organization_id: z.string(),
  brands: z.array(
    z.object({
      brand_id: z.string(),
      rentals: z.array(
        z.object({
          door_id: z.string(),
        }),
      ),
    }),
  ),
  corporation_id: z.string(),
  person_id: z.string(),
  roles: z.array(z.string()),
})

export function useIsAuthorized() {
  const { isAuthenticated, isLoading, error, user } = useAuth0()
  const dataAccessPath = `${CONFIG.AUTH0_AUDIENCE}/data_access`
  const userHasDataAccessClaim = user && user[dataAccessPath] !== undefined

  const authenticated = isAuthenticated && !!user && !error

  return {
    isLoading,
    isAuthenticated: authenticated,
    isAuthorized: !isLoading && authenticated && !!userHasDataAccessClaim,
    error,
  }
}

export function useUser() {
  const auth0 = useAuth0()
  const { user, isLoading } = auth0

  const data = useMemo(() => {
    if (user) {
      return {
        user: user,
        dataAccess: user[dataAccessKey]
          ? dataAccessSchema.parse(user[dataAccessKey])
          : undefined,
      } as const
    }

    if (!isLoading) {
      return null
    }

    return undefined
  }, [isLoading, user])

  return {
    auth0,
    data,
  }
}
